import { useCallback, useState } from 'react';

import FullScreenBanner from '@goshippo/libraries/shared/FullScreenBanner';

import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { OtcVerificationDialog } from '~/src/components/OtcVerificationDialog/OtcVerificationDialog';
import { useBillingAccount } from '~/src/hooks/useBillingAccount';
import { useGlobalAlerts } from '~/src/hooks/useGlobalAlerts';
import { useUserAccount } from '~/src/hooks/useUserAccount';
import {
  BILLING_BLOCKED_STATUS_ENUM,
  BillingBlockedStatuses,
} from '~/src/services/UserAccountService';
import { menuLinks } from '~/src/utils/menu';

const getBannerMessage = (
  t: TFunction,
  isGracePeriodExpired: boolean,
  billingBlockedStatus?: BillingBlockedStatuses,
) => {
  if (billingBlockedStatus === BILLING_BLOCKED_STATUS_ENUM.BLOCKED)
    return t('pastDueInvoices.billingBlocked');
  if (isGracePeriodExpired)
    return `${t('pastDueInvoices.gracePeriod')} ${t('pastDueInvoices.message')}`;
  return t('pastDueInvoices.message');
};

export const FullScreenBannerWrapper = () => {
  useGlobalAlerts();

  const { t } = useTranslation('', { keyPrefix: 'fullScreenBanner' });
  const navigate = useNavigate();
  const billingAccount = useBillingAccount().data;
  const { data, refetch } = useUserAccount();
  const [isOtcDialogOpen, setIsOtcDialogOpen] = useState(false);

  const handleGoToBilling = useCallback(() => {
    navigate('/account/billing');
  }, [navigate]);

  const handleGoToSupport = useCallback(() => {
    window.open(
      `${menuLinks.customerSupport}/hc/en-us/categories/4403004943643-Account-Billing`,
      '_blank',
      'noopener,noreferrer',
    );
  }, []);

  const isGracePeriodExpired = billingAccount?.grace_period_end_timestamp
    ? new Date(billingAccount.grace_period_end_timestamp) < new Date()
    : false;

  if (data?.requiresEmailVerification) {
    return (
      <>
        <FullScreenBanner
          actionFn={() => setIsOtcDialogOpen(true)}
          actionLabel={t('verifyEmail.actionLabel')}
          bannerType="notice"
          message={t('verifyEmail.message')}
        />
        <OtcVerificationDialog
          email={data.email}
          isOpen={isOtcDialogOpen}
          onClose={() => setIsOtcDialogOpen(false)}
          onSuccess={() => {
            setIsOtcDialogOpen(false);
            refetch();
          }}
        />
      </>
    );
  } else if (
    (data?.billingBlockedStatus &&
      data?.billingBlockedStatus !== BILLING_BLOCKED_STATUS_ENUM.UNBLOCKED) ||
    isGracePeriodExpired
  ) {
    const message = getBannerMessage(t, isGracePeriodExpired, data?.billingBlockedStatus);
    const billingBlocked = data?.billingBlockedStatus === BILLING_BLOCKED_STATUS_ENUM.BLOCKED;

    return (
      <FullScreenBanner
        actionFn={billingBlocked ? handleGoToSupport : handleGoToBilling}
        actionLabel={
          billingBlocked
            ? t('pastDueInvoices.supportActionLabel')
            : t('pastDueInvoices.actionLabel')
        }
        bannerType="notice"
        message={message}
      />
    );
  }
  return null;
};
